import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { HERO } from "@/constants/testIds";
import { ArrowDownRight, ArrowUpRight, Linkedin, BookOpen } from "lucide-react";
import ExecutiveSummary from "@/components/ExecutiveSummary";

const PORTRAIT_URL = "/asad_hero_portrait.jpg";

const lines = [
  { text: "Asad", accent: false },
  { text: "Hatimi", accent: false },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } },
};
const lineVariants = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 1.1, ease: [0.77, 0, 0.175, 1] } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero({ onContactClick }) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 450);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      data-testid={HERO.container}
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Subtle vignette */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(184,147,90,0.10),transparent_60%)]" />

      {/* Top rail */}
      <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-16 pt-40 md:pt-48">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-brass"
        >
          <span className="tabular">01 —</span>
          <span>Industrial Project Leadership · Est. 2004</span>
          <span className="ml-2 h-px w-16 bg-brass/40" />
        </motion.div>

        <div className="mt-8 grid grid-cols-12 gap-6 md:gap-10">
          {/* Left — Kinetic name */}
          <div className="col-span-12 lg:col-span-8">
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="font-serif-display font-light leading-[0.85] tracking-[-0.03em] text-ivory text-[18vw] md:text-[13vw] lg:text-[11vw]"
            >
              {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span variants={lineVariants} className="block will-change-transform">
                    {line.text}
                    {i === 1 && (
                      <span className="ml-3 align-top text-brass italic font-normal text-[0.35em]">.</span>
                    )}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ delay: 0.9 }}
              className="mt-10 max-w-xl font-sans font-light text-mutedfg text-lg leading-relaxed"
            >
              Industrial project management and CAPEX governance leader with{" "}
              <span className="text-ivory">21+ years</span> steering capital-intensive programmes across
              cement, steel, power and EPC. Available for{" "}
              <span className="text-brass">project advisory and recovery mandates</span>, independent board
              stewardship, and expert consulting through <span className="text-brass">GLG</span> and{" "}
              <span className="text-brass">Guidepoint</span>.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ delay: 1.05 }}
              className="mt-12 flex flex-wrap items-center gap-4"
            >
              <a
                href="/Asad_Hatimi_CV.pdf"
                download
                data-testid={HERO.downloadCv}
                className="group inline-flex items-center gap-3 bg-brass px-8 py-4 text-ink text-[13px] uppercase tracking-[0.2em] font-semibold transition-transform duration-500 hover:-translate-y-[2px]"
              >
                Download Curriculum
                <ArrowDownRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" />
              </a>
              <ExecutiveSummary>
                <button
                  type="button"
                  data-testid={HERO.execSummary}
                  className="group inline-flex items-center gap-3 border border-brass/70 px-8 py-4 text-brass text-[13px] uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-brass hover:text-ink"
                >
                  <BookOpen size={14} /> Executive Summary
                </button>
              </ExecutiveSummary>
              <button
                onClick={onContactClick}
                data-testid={HERO.contact}
                className="group inline-flex items-center gap-3 border border-ivory/25 px-8 py-4 text-ivory text-[13px] uppercase tracking-[0.2em] transition-colors duration-500 hover:border-brass hover:text-brass"
              >
                Discuss a Mandate
                <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <a
                href="https://www.linkedin.com/in/asad-hatimi-program-leader"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HERO.linkedin}
                className="inline-flex items-center gap-2 text-mutedfg hover:text-brass link-underline text-[13px] uppercase tracking-[0.2em] ml-2"
              >
                <Linkedin size={14} /> LinkedIn
              </a>
            </motion.div>
          </div>

          {/* Right — Portrait */}
          <div className="col-span-12 lg:col-span-4 relative mt-8 lg:mt-0">
            <div className="relative aspect-[3/4] w-full max-w-[380px] ml-auto overflow-hidden border border-surface-2">
              <div className="absolute inset-0">
                <div className={`portrait-clip absolute inset-0 ${revealed ? "revealed" : ""}`}>
                  <img
                    src={PORTRAIT_URL}
                    alt="Asad Hatimi — Executive Portrait"
                    width="384"
                    height="512"
                    className="h-full w-full object-cover"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              </div>
              {/* spotlight overlay */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_20%,rgba(10,22,40,0.55)_75%)]" />
              {/* corner marks */}
              <span className="absolute left-0 top-0 h-4 w-4 border-l border-t border-brass" />
              <span className="absolute right-0 top-0 h-4 w-4 border-r border-t border-brass" />
              <span className="absolute left-0 bottom-0 h-4 w-4 border-l border-b border-brass" />
              <span className="absolute right-0 bottom-0 h-4 w-4 border-r border-b border-brass" />
            </div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ delay: 1.3 }}
              className="mt-6 max-w-[380px] ml-auto text-right"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-brass mb-2">In Practice</div>
              <div className="font-serif-display italic text-ivory text-lg leading-snug">
                &ldquo;Governance is not oversight after the fact — it is the shape of the plan.&rdquo;
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Ambient credentials — bottom */}
      <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-16 mt-16 pb-16">
        <div className="hairline mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-[11px] uppercase tracking-[0.22em] text-mutedfg">
          <div><span className="text-brass tabular">₹3,200+ Cr</span><div className="mt-1">CAPEX Overseen</div></div>
          <div><span className="text-brass tabular">15</span><div className="mt-1">Programs Commissioned</div></div>
          <div><span className="text-brass tabular">21+</span><div className="mt-1">Years of Practice</div></div>
          <div><span className="text-brass tabular">04</span><div className="mt-1">Industrial Groups</div></div>
        </div>
      </div>
    </section>
  );
}
